import type { NewsItem } from '../types/news.types'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { ExternalLink, Globe, Calendar } from 'lucide-react'
import { formatDate } from '@/lib/utils'
import { ShareButton } from './ShareButton'
import { HighlightedText } from './HighlightedText'
import { sourceHost, originalUrl } from '../utils/sourceUtils'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Helper Functions
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function cleanHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/&quot;/g, '"')
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// NewsCard Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

interface NewsCardProps {
  item: NewsItem
  keyword?: string
}

export function NewsCard({ item, keyword }: NewsCardProps) {
  const source = sourceHost(item)
  // 제목은 사용자가 고른 읽기 경로(`link`)로, "원문 보기"는 말 그대로 원문으로 보낸다.
  // 서버 렌더 페이지의 `article_meta()`와 같은 배분이다.
  const originalHref = originalUrl(item)
  const title = cleanHtml(item.title)
  const description = cleanHtml(item.description)
  const publishedDate = formatDate(item.pub_date)

  // Prepare keywords for highlighting (support multiple space-separated keywords)
  const keywords = keyword ? keyword.split(/\s+/).filter((k) => k.trim()) : []

  return (
    <Card className="group h-full transition-all hover:shadow-lg">
      <CardHeader className="pb-3">
        <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
          <Globe className="h-3 w-3" />
          <span>{source}</span>
        </div>
        <a
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          className="text-lg font-semibold leading-tight transition-colors hover:text-primary"
        >
          <HighlightedText text={title} keywords={keywords} />
        </a>
      </CardHeader>

      <CardContent className="pb-3">
        <p className="line-clamp-3 text-sm text-muted-foreground">
          <HighlightedText text={description} keywords={keywords} />
        </p>
      </CardContent>

      <CardFooter className="flex items-center justify-between border-t pt-3">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Calendar className="h-3 w-3" />
          <span>{publishedDate}</span>
        </div>
        <div className="flex items-center gap-2">
          <ShareButton article={item} />
          <a
            href={originalHref}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:text-primary/80"
          >
            <span>원문 보기</span>
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </CardFooter>
    </Card>
  )
}
